import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from "js-cookie";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useAuth } from "context/AuthContext";
import { apiRequest } from "utils/api";
import { useLanguage } from "context/LanguageContext";
import { uuidToConsulta9 } from '../../utils/uuidConverter';
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import PageBlueHeader from 'components/PageBlueHeader';
import { ACCENT } from 'layouts/authentication/constants/loginTheme';

const cardSx = {
    borderRadius: '18px',
    overflow: 'hidden',
    boxShadow: '0 16px 40px -12px rgba(52, 71, 103, 0.18)',
    border: '1px solid rgba(233, 236, 239, 0.95)',
    backgroundColor: '#fff',
};

const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        width: '100%',
    },
    '& .MuiInputBase-input': {
        fontSize: '0.9rem',
        py: 1.2,
        width: '100% !important',
        boxSizing: 'border-box',
    },
    '& .MuiInputBase-inputMultiline': {
        width: '100% !important',
        boxSizing: 'border-box',
    },
    '& textarea': {
        width: '100% !important',
        boxSizing: 'border-box',
    },
    '& .MuiOutlinedInput-input': {
        paddingLeft: '14px !important',
        paddingRight: '14px !important',
    },
};

const textareaSx = {
    ...inputSx,
    '& .MuiOutlinedInput-root': {
        ...inputSx['& .MuiOutlinedInput-root'],
        alignItems: 'flex-start',
        padding: '12px 0',
    },
    '& .MuiInputBase-inputMultiline': {
        width: '100% !important',
        boxSizing: 'border-box',
        padding: '0 14px !important',
        resize: 'vertical',
    },
    '& textarea': {
        width: '100% !important',
        boxSizing: 'border-box',
    },
};

const Soporte = () => {
    const [titulo, setTitulo] = useState('');
    const [detalle, setDetalle] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();

    const { user, loading } = useAuth();
    const { t } = useLanguage();

    useEffect(() => {
        if (loading) return;
        if (!user) navigate("/authentication/sign-in");
    }, [navigate, user, loading]);

    const registrarAccion = async (accion, detalles = '') => {
        try {
          const userId = Cookies.get('userId');
          if (!userId) return;

          const response = await apiRequest('/registrar-accion', {
            method: 'POST',
            body: JSON.stringify({
                userId,
                accion,
                detalles: detalles || '',
                origen: 'empresas',
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Error desconocido');
          }

          return response.json();
        } catch (actionError) {
          console.error('Error al registrar la acción:', actionError.message);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');

        try {
            const userResponse = await apiRequest('/datos-usuario');
            const userData = await userResponse.json();

            const response = await apiRequest('/crear-reclamo', {
                method: 'POST',
                body: JSON.stringify({
                    titulo,
                    detalle,
                    documento: userData.documento,
                    prioridad: 'media',
                }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Error al crear el reclamo');
            }

            const numeroConsulta = uuidToConsulta9(data.reclamoId);

            try {
                await fetch('https://hook.us1.make.com/y726vbfvfqskmh95vwx29m5ce7s5i4bu', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        mail: userData.email,
                        numeroConsulta,
                    }),
                });
            } catch (webhookError) {
                console.error('Error al enviar al webhook:', webhookError);
            }

            setSuccess(true);
            await registrarAccion('Enviar consulta a soporte', `${titulo}`);
            setTitulo('');
            setDetalle('');
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleNewInquiry = () => {
        setSuccess(false);
        setError('');
    };

    return (
        <DashboardLayout>
            <PageBlueHeader
                title={t('support.title')}
                subtitle={t('support.description')}
                subtitleNoWrap
            />

            <ArgonBox pt={2} pb={6}>
                <Card sx={cardSx}>
                    <ArgonBox
                        sx={{
                            p: { xs: 2.5, sm: 3 },
                            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                            borderBottom: '1px solid rgba(30, 134, 246, 0.08)',
                        }}
                    >
                        <ArgonTypography
                            variant="h5"
                            sx={{
                                fontSize: { xs: '1.15rem', sm: '1.35rem' },
                                fontWeight: 700,
                                color: '#344767',
                                letterSpacing: '-0.02em',
                            }}
                        >
                            {t('support.formTitle')}
                        </ArgonTypography>
                        <ArgonTypography
                            variant="caption"
                            sx={{
                                display: 'block',
                                mt: 0.5,
                                fontSize: '0.8rem',
                                color: '#67748e',
                                fontWeight: 500,
                            }}
                        >
                            {t('support.formSubtitle')}
                        </ArgonTypography>
                    </ArgonBox>

                    <ArgonBox p={{ xs: 2.5, sm: 3 }}>
                        {success ? (
                            <ArgonBox textAlign="center" py={3}>
                                <CheckCircleOutlineIcon sx={{ fontSize: 52, color: '#2f855a', mb: 1.5 }} />
                                <ArgonTypography
                                    variant="h5"
                                    sx={{ fontWeight: 700, color: '#276749', mb: 1, fontSize: '1.2rem' }}
                                >
                                    {t('support.successTitle')}
                                </ArgonTypography>
                                <ArgonTypography
                                    variant="body2"
                                    sx={{ color: '#67748e', mb: 2.5, maxWidth: 420, mx: 'auto' }}
                                >
                                    {t('support.successDesc')}
                                </ArgonTypography>
                                <Button
                                    variant="outlined"
                                    onClick={handleNewInquiry}
                                    sx={{
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderColor: 'rgba(30, 134, 246, 0.35)',
                                        color: ACCENT,
                                        '&:hover': {
                                            borderColor: ACCENT,
                                            bgcolor: 'rgba(30, 134, 246, 0.06)',
                                        },
                                    }}
                                >
                                    {t('support.newInquiry')}
                                </Button>
                            </ArgonBox>
                        ) : (
                            <ArgonBox
                                component="form"
                                onSubmit={handleSubmit}
                                display="flex"
                                flexDirection="column"
                                gap={2.5}
                            >
                                <ArgonBox>
                                    <ArgonTypography
                                        variant="body2"
                                        sx={{ fontWeight: 700, mb: 1, fontSize: '0.8rem', color: '#344767' }}
                                    >
                                        {t('support.subjectLabel')}
                                    </ArgonTypography>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        value={titulo}
                                        onChange={(e) => setTitulo(e.target.value)}
                                        placeholder={t('support.subjectPlaceholder')}
                                        required
                                        sx={inputSx}
                                    />
                                </ArgonBox>

                                <ArgonBox>
                                    <ArgonTypography
                                        variant="body2"
                                        sx={{ fontWeight: 700, mb: 1, fontSize: '0.8rem', color: '#344767' }}
                                    >
                                        {t('support.detailLabel')}
                                    </ArgonTypography>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        value={detalle}
                                        onChange={(e) => setDetalle(e.target.value)}
                                        placeholder={t('support.detailPlaceholder')}
                                        required
                                        multiline
                                        rows={5}
                                        sx={textareaSx}
                                    />
                                </ArgonBox>

                                {error && (
                                    <ArgonTypography
                                        variant="body2"
                                        sx={{
                                            color: '#c62828',
                                            bgcolor: '#fff5f5',
                                            border: '1px solid rgba(198, 40, 40, 0.15)',
                                            borderRadius: '8px',
                                            px: 2,
                                            py: 1.25,
                                            fontSize: '0.875rem',
                                        }}
                                    >
                                        {error}
                                    </ArgonTypography>
                                )}

                                <ArgonBox display="flex" justifyContent="flex-end" mt={0.5}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={isSubmitting}
                                        sx={{
                                            minWidth: 140,
                                            py: 1.1,
                                            px: 3,
                                            borderRadius: '8px',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: '0.9rem',
                                            bgcolor: ACCENT,
                                            boxShadow: 'none',
                                            '&:hover': {
                                                bgcolor: '#1570d4',
                                                boxShadow: 'none',
                                            },
                                        }}
                                    >
                                        {isSubmitting ? (
                                            <CircularProgress size={20} sx={{ color: '#fff' }} />
                                        ) : (
                                            t('support.submit')
                                        )}
                                    </Button>
                                </ArgonBox>
                            </ArgonBox>
                        )}
                    </ArgonBox>
                </Card>
            </ArgonBox>
        </DashboardLayout>
    );
};

export default Soporte;
